/**
 * 位置评估模块
 * 综合评估棋局局面
 */
import { PatternRecognizer } from './patterns.js';

export class PositionEvaluator {
    constructor() {
        this.patternRecognizer = new PatternRecognizer();
        this.weights = {
            attack: 1.0,    // 进攻权重
            defense: 1.1,   // 防守权重（略高于进攻）
            center: 1.2,    // 中心控制权重
            mobility: 0.5   // 机动性权重
        };
    }

    /**
     * 评估当前局面
     * @param {Board} board 棋盘
     * @returns {number} 评估分数
     */
    evaluate(board) {
        // 如果游戏结束，返回极值
        if (board.gameOver) {
            if (board.winner === board.currentPlayer) {
                return 100000; // 当前玩家获胜
            } else if (board.winner === 3 - board.currentPlayer) {
                return -100000; // 对手获胜
            } else {
                return 0; // 平局
            }
        }

        // 当前玩家评分
        const currentScore = this.patternRecognizer.evaluatePosition(board, board.currentPlayer);

        // 对手评分
        const opponentScore = this.patternRecognizer.evaluatePosition(board, 3 - board.currentPlayer);

        // 综合评分
        const attackScore = currentScore * this.weights.attack;
        const defenseScore = opponentScore * this.weights.defense;
        const centerScore = this.evaluateCenterControl(board, board.currentPlayer) * this.weights.center;
        const mobilityScore = this.evaluateMobility(board) * this.weights.mobility;

        const totalScore = attackScore - defenseScore + centerScore + mobilityScore;

        return totalScore;
    }

    /**
     * 评估中心控制
     * @param {Board} board 棋盘
     * @param {number} player 玩家
     * @returns {number} 中心控制分数
     */
    evaluateCenterControl(board, player) {
        const center = Math.floor(board.size / 2);
        let score = 0;

        // 中心区域控制
        for (let r = Math.max(0, center - 2); r <= Math.min(board.size - 1, center + 2); r++) {
            for (let c = Math.max(0, center - 2); c <= Math.min(board.size - 1, center + 2); c++) {
                if (board.board[r][c] === player) {
                    // 距离中心越近，分值越高
                    const distance = Math.abs(r - center) + Math.abs(c - center);
                    score += (5 - distance) * 10;
                }
            }
        }

        return score;
    }

    /**
     * 评估机动性（可选走法数量）
     * @param {Board} board 棋盘
     * @returns {number} 机动性分数
     */
    evaluateMobility(board) {
        const emptyPositions = board.getEmptyPositions();
        const relevantMoves = [];

        for (const pos of emptyPositions) {
            if (this.isRelevantMove(board, pos)) {
                relevantMoves.push(pos);
            }
        }

        return relevantMoves.length;
    }

    /**
     * 判断走法是否有意义（附近有棋子）
     * @param {Board} board 棋盘
     * @param {Object} pos 位置
     * @returns {boolean} 是否有意义
     */
    isRelevantMove(board, pos) {
        const { row, col } = pos;

        for (let dr = -2; dr <= 2; dr++) {
            for (let dc = -2; dc <= 2; dc++) {
                const r = row + dr;
                const c = col + dc;
                if (r >= 0 && r < board.size && c >= 0 && c < board.size && board.board[r][c] !== 0) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * 快速评估走法价值
     * @param {Board} board 棋盘
     * @param {Object} pos 位置
     * @returns {number} 走法价值
     */
    quickEvaluateMove(board, pos) {
        const { row, col } = pos;
        const player = board.currentPlayer;
        const opponent = 3 - player;
        let score = 0;

        // 评估该位置对己方的价值
        const attackScore = this.patternRecognizer.quickEvaluatePosition(board, row, col, player);
        score += attackScore;

        // 评估该位置对对手的威胁（防守价值）
        const defenseScore = this.patternRecognizer.quickEvaluatePosition(board, row, col, opponent);
        score += defenseScore * 0.8; // 防守权重略低

        // 中心位置加分
        const center = Math.floor(board.size / 2);
        const distance = Math.abs(row - center) + Math.abs(col - center);
        score += (7 - distance) * 2;

        return score;
    }

    /**
     * 获取智能候选走法（考虑策略优先级）
     * @param {Board} board 棋盘
     * @param {number} maxCandidates 最大候选数
     * @returns {Array} 候选走法数组
     */
    getCandidateMoves(board, maxCandidates = 25) {
        const emptyPositions = board.getRelevantEmptyPositions();
        const scoredMoves = [];
        const currentPlayer = board.currentPlayer;
        const opponent = 3 - currentPlayer;

        for (const pos of emptyPositions) {
            // 检查是否为禁手（黑棋）
            if (this.patternRecognizer.isForbiddenMove(board, pos.row, pos.col, currentPlayer)) {
                continue;
            }

            let strategicScore = 0;
            let moveType = 'normal';

            // 检查我方是否能形成双活三
            if (this.patternRecognizer.hasDoubleOpenThree(board, pos.row, pos.col, currentPlayer)) {
                strategicScore += 15000;
                moveType = 'double_open_three';
            }

            // 检查对手威胁
            const opponentThreat = this.patternRecognizer.detectThreats(board, pos.row, pos.col, opponent);
            if (opponentThreat.isUrgent) {
                // 必须防守时，优先级最高
                if (opponentThreat.hasOpenFour) {
                    strategicScore += 50000;
                    moveType = 'defend_open_four';
                } else if (opponentThreat.hasBlockedFour && opponentThreat.hasOpenThree) {
                    strategicScore += 20000;
                    moveType = 'defend_complex_threat';
                } else if (opponentThreat.hasOpenThree) {
                    strategicScore += 8000;
                    moveType = 'defend_open_three';
                }
            }

            // 如果没有紧急威胁，优先考虑进攻性活三
            if (!opponentThreat.isUrgent) {
                // 检查我方活三
                let myOpenThreeCount = 0;
                board.board[pos.row][pos.col] = currentPlayer;
                for (const direction of this.patternRecognizer.directions) {
                    const { count, block, space } = this.patternRecognizer.countPattern(board, pos.row, pos.col, currentPlayer, direction);
                    if (count === 3 && block === 0 && space.length >= 2) {
                        myOpenThreeCount++;
                    }
                }
                board.board[pos.row][pos.col] = 0;

                if (myOpenThreeCount >= 1) {
                    if (myOpenThreeCount >= 2) {
                        strategicScore += 12000; // 双活三
                        moveType = 'my_double_open_three';
                    } else {
                        strategicScore += 3000; // 单活三
                        moveType = 'my_open_three';
                    }
                }

                // 只有在有明确进攻优势时才考虑冲四
                if (moveType === 'normal') {
                    board.board[pos.row][pos.col] = currentPlayer;
                    let hasFour = false;
                    for (const direction of this.patternRecognizer.directions) {
                        const { count, block } = this.patternRecognizer.countPattern(board, pos.row, pos.col, currentPlayer, direction);
                        if (count >= 4) {
                            hasFour = true;
                            break;
                        }
                    }
                    board.board[pos.row][pos.col] = 0;

                    if (hasFour) {
                        // 只有在冲四能立即获胜或创造明显优势时才考虑
                        board.board[pos.row][pos.col] = currentPlayer;
                        const testBoard = board.clone();
                        testBoard.board[pos.row][pos.col] = currentPlayer;
                        testBoard.currentPlayer = opponent;

                        // 检查对手能否立即防守
                        let canDefend = false;
                        const opponentPositions = testBoard.getRelevantEmptyPositions();
                        for (const oppPos of opponentPositions) {
                            testBoard.board[oppPos.row][oppPos.col] = opponent;
                            let stillThreat = false;
                            for (const direction of this.patternRecognizer.directions) {
                                const { count, block } = this.patternRecognizer.countPattern(testBoard, pos.row, pos.col, currentPlayer, direction);
                                if (count >= 4) {
                                    stillThreat = true;
                                    break;
                                }
                            }
                            testBoard.board[oppPos.row][oppPos.col] = 0;
                            if (!stillThreat) {
                                canDefend = true;
                                break;
                            }
                        }
                        board.board[pos.row][pos.col] = 0;

                        if (!canDefend) {
                            strategicScore += 10000;
                            moveType = 'forced_four';
                        } else {
                            strategicScore += 500; // 防守性冲四
                            moveType = 'defensive_four';
                        }
                    }
                }
            }

            // 基础位置价值评估
            const baseScore = this.quickEvaluateMove(board, pos);
            const finalScore = strategicScore + baseScore;

            scoredMoves.push({
                score: finalScore,
                ...pos,
                type: moveType,
                strategicScore
            });
        }

        // 按策略优先级排序
        scoredMoves.sort((a, b) => {
            // 优先级顺序：防守 > 双活三 > 进攻活三 > 强制冲四 > 防守冲四 > 其他
            const typePriority = {
                'defend_open_four': 100,
                'defend_complex_threat': 90,
                'my_double_open_three': 85,
                'double_open_three': 80,
                'defend_open_three': 70,
                'my_open_three': 60,
                'forced_four': 50,
                'defensive_four': 30,
                'normal': 0
            };

            const priorityA = typePriority[a.type] || 0;
            const priorityB = typePriority[b.type] || 0;

            if (priorityA !== priorityB) {
                return priorityB - priorityA;
            }

            // 相同类型按分数排序
            return b.score - a.score;
        });

        return scoredMoves.slice(0, maxCandidates);
    }
}